import type { InlineCode, Parent, Root, Text } from 'mdast';
import { findReferences } from '../../services/excel/cellRefs';

/** Parents whose text must stay text: a link already goes somewhere. */
const KEEP = new Set(['link', 'linkReference']);

/**
 * A remark plugin that turns a workbook reference written in plain prose into a code span, which `CellLink`
 * then renders as a link — the same markup the pane contract asks the agent for, supplied when it forgot.
 *
 * `findReferences` decides what is a reference: it needs a sheet the workbook has, so prose `P50` stays prose
 * and nothing is touched while the sheet list is still empty.
 */
export function remarkCellRefs(sheets: readonly string[]) {
  return () => (tree: Root) => {
    if (sheets.length) visit(tree, sheets);
  };
}

function visit(node: Parent, sheets: readonly string[]): void {
  if (KEEP.has(node.type)) return;
  const out: Parent['children'] = [];
  for (const child of node.children) {
    if (child.type === 'text') out.push(...split(child, sheets));
    else {
      if ('children' in child) visit(child, sheets);
      out.push(child);
    }
  }
  node.children = out;
}

function split(node: Text, sheets: readonly string[]): Array<Text | InlineCode> {
  const found = findReferences(node.value, sheets);
  if (!found.length) return [node];
  const parts: Array<Text | InlineCode> = [];
  let last = 0;
  for (const { start, end } of found) {
    if (start > last) parts.push({ type: 'text', value: node.value.slice(last, start) });
    parts.push({ type: 'inlineCode', value: node.value.slice(start, end) });
    last = end;
  }
  if (last < node.value.length) parts.push({ type: 'text', value: node.value.slice(last) });
  return parts;
}
